import React, { MouseEventHandler, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { toAbsoluteUrlForTracking } from '@lib/toAbsoluteUrlForTracking';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { useMessageTrackingContext } from '@pages/MyMessages/contexts/MessageTrackingContext';
import { DocumentTypeLabel } from '@appTypes/testType';
import { InternalLinkInDialog } from './InternalLinkInDialog';

interface InternalLinkProps {
  value?: {
    referenceComponent: { slug: string; type: DocumentTypeLabel; name: string };
    anchorId?: string;
    queryParams: { value: string; key: string }[];
    openInDialog: boolean;
  };
  children: ReactNode | ReactNode[] | string | string[];
}

export const InternalLinkMarks = ({
  value: { referenceComponent, anchorId, queryParams, openInDialog },
  children,
}: InternalLinkProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dataLayer = useGTMDataLayer();
  const messageContext = useMessageTrackingContext();
  const hostname = (typeof window !== 'undefined' && window.location.hostname) || undefined;
  const href = useDocumentUrlWithNode(referenceComponent, anchorId, queryParams) || '';

  if (!referenceComponent?.slug) return children;

  if (openInDialog) {
    const trackingFunction: MouseEventHandler<HTMLAnchorElement> = (e) => {
      e.preventDefault();
      dataLayer?.push({
        event: messageContext ? NAFGTMEvent.mittNafMessageCtaClick : 'link_click',
        link_text: Array.isArray(children) ? children.join(' ') : children.toString(),
        link_location: location.pathname,
        link_url: toAbsoluteUrlForTracking(href),
        link_hostname: hostname,
        ...(messageContext?.messageId ? { message_id: messageContext.messageId } : {}),
        ...(messageContext?.messageType ? { message_type: messageContext.messageType } : {}),
        ...(messageContext?.messageTitle ? { message_title: messageContext.messageTitle } : {}),
      });
    };

    return (
      <InternalLinkInDialog href={href} trackingFunction={trackingFunction}>
        {children}
      </InternalLinkInDialog>
    );
  }

  if (referenceComponent.type === 'nafMembershipBenefit' && referenceComponent.slug) {
    const handleBenefitCTATracking: MouseEventHandler<HTMLAnchorElement> = (e) => {
      e.preventDefault();
      dataLayer?.push({
        event: messageContext ? NAFGTMEvent.mittNafMessageCtaClick : 'link_click',
        link_text: referenceComponent.name,
        link_location: location.pathname,
        link_url: toAbsoluteUrlForTracking(href),
        link_hostname: hostname,
        ...(messageContext?.messageId ? { message_id: messageContext.messageId } : {}),
        ...(messageContext?.messageType ? { message_type: messageContext.messageType } : {}),
        ...(messageContext?.messageTitle ? { message_title: messageContext.messageTitle } : {}),
      });
      navigate(href);
    };
    return (
      <Link to={href} onClick={handleBenefitCTATracking}>
        {children}
      </Link>
    );
  }

  const handleOtherDocumentsCTATracking: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    dataLayer?.push({
      event: messageContext ? NAFGTMEvent.mittNafMessageCtaClick : 'link_click',
      link_text: Array.isArray(children) ? children.join(' ') : children.toString(),
      link_location: location.pathname,
      link_url: toAbsoluteUrlForTracking(href),
      link_hostname: hostname,
      ...(messageContext?.messageId ? { message_id: messageContext.messageId } : {}),
      ...(messageContext?.messageType ? { message_type: messageContext.messageType } : {}),
      ...(messageContext?.messageTitle ? { message_title: messageContext.messageTitle } : {}),
    });
    navigate(href);
  };

  return (
    <Link to={href} onClick={handleOtherDocumentsCTATracking}>
      {children}
    </Link>
  );
};
